import { Component, OnInit, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import {
  BodyanalyticsBaseComponent,
  SessionStorageService,
  BrandLogoComponent,
  ThemeService,
} from '@bodyanalytics/ui-core';
import { LoginService } from '../../services/login.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { RouterUrlsEnum } from '@bodyanalytics/ui-core';
import { tap } from 'rxjs';
import { ENV } from '@bodyanalytics/app-config';
import { MemberLoginPanelComponent } from '../../components/member-login-panel/member-login-panel.component';

/** Time-of-day greeting for the admin/staff login panel — a small warm touch in an
 *  otherwise sparse single-column card, not tied to any identity (nobody's signed in yet). */
function staffGreetingForHour(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) {
    return 'morning';
  }
  if (hour < 18) {
    return 'afternoon';
  }
  return 'evening';
}

@Component({
  selector: 'bodyanalytics-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [RouterLink, BrandLogoComponent, MemberLoginPanelComponent],
})
export class LoginComponent extends BodyanalyticsBaseComponent implements OnInit {
  private sessionSessionService = inject(SessionStorageService);
  private activatedRoute = inject(ActivatedRoute);
  auth = inject(AuthService);
  protected readonly theme = inject(ThemeService);
  private loginService = inject(LoginService);
  private router = inject(Router);
  private appConfig = inject(ENV);

  readonly staffGreeting = staffGreetingForHour(new Date().getHours());

  returnUrl = '';
  ngOnInit(): void {
    const isCustomerPortal = this.appConfig.portalName === 'customer';
    const bookingData = this.sessionSessionService.getBookingData();
    const workflowStep = bookingData?.workflowStep ?? 'search';
    const eventId = bookingData?.selectedEvent?.id;

    switch (workflowStep) {
      case 'checkout':
        this.returnUrl = `${RouterUrlsEnum.CALENDER_CHECKOUT}/${eventId}`;
        break;
      case 'signup-verify':
        this.returnUrl = `${RouterUrlsEnum.CALENDAR_SIGNUP_VERIFY}/${eventId}`;
        break;
      case 'signup':
        // #1933 — a user mid-signup (workflowStep === 'signup') must return to
        // THEIR event's signup form after login, not fall through to the generic
        // search page. (CALENDER_SIGNUP has no trailing slash, so add one.)
        this.returnUrl = `${RouterUrlsEnum.CALENDER_SIGNUP}/${eventId}`;
        break;
      case 'search':
        this.returnUrl = `${RouterUrlsEnum.CALENDER_EVENTS_SEARCH}`;
        break;
      default:
        this.returnUrl = `${RouterUrlsEnum.CALENDER_EVENTS_SEARCH}`; // Define a default URL if needed
        break;
    }

    // Customer portal (#840): there is no calendar booking funnel in this app, so a
    // signed-in member with no active booking workflow should land on their member
    // dashboard rather than the calendar search route (which isn't mounted here).
    if (isCustomerPortal && workflowStep === 'search') {
      this.returnUrl = RouterUrlsEnum.AUTH_DASHBOARD;
    }

    // Admin portal (#2119): the staff portal has no booking funnel at all, and this
    // shared screen is now admin's default pre-auth landing (the bespoke
    // AdminHomeComponent duplicate was deleted). A signed-in admin always lands on
    // the admin dashboard — the calendar routes above aren't mounted in that app.
    if (this.isAdminPortal()) {
      this.returnUrl = RouterUrlsEnum.AUTH_ADMIN_DASHBOARD;
    }

    this.subscriptions.barrel = this.auth.isAuthenticated$
      .pipe(
        tap(loggedIn => {
          if (loggedIn) {
            this.router.navigateByUrl(this.returnUrl);
          } else if (isCustomerPortal || this.isAdminPortal()) {
            // Stay on /login so the member/staff user can trigger Auth0 via the
            // login button; don't bounce into a calendar funnel that isn't part
            // of these apps (#840 customer, #2119 admin).
            return;
          } else if (this.returnUrl.includes('signup-verify')) {
            // #2126 — this must be an explicit boolean. The old `indexOf(...)` truthy
            // check was inverted: -1 (absent) is truthy, so every non-verify visitor
            // was bounced to /login instead of the calendar search, and an index-0
            // match (path starting with 'signup-verify') is falsy, destroying the
            // verify deep link's session. Keep the signup-verify booking session
            // intact and stay on /login so the user can authenticate.
            this.router.navigate([`${RouterUrlsEnum.LOGIN}`]);
          } else {
            this.router.navigate([`${RouterUrlsEnum.CALENDER_EVENTS_SEARCH}`]);
            this.sessionSessionService.clear();
          }
        }),
      )
      .subscribe();
  }

  // public login(login: LoginI) {
  //   this.subscriptions.barrel = this.loginService
  //     .login(login)

  //     .subscribe();
  // }

  signup() {
    this.loginService.setReturnUrl(this.returnUrl);
    this.router.navigate([`${RouterUrlsEnum.AUTH_REGISTER_ACCOUNT}`]);
  }

  public isAdminPortal(): boolean {
    if (this.appConfig.portalName === 'admin') {
      return true;
    }
    return false;
  }
}
